import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, PhoneCall, Truck, ChevronDown, ChevronRight, Music2, RotateCcw } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsShopOpen(false);
  }, [location]);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Support', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Utility bar (graphite) */}
      <div className="bg-dark-900 text-dark-200 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="flex items-center gap-2 tracking-wide">
            <Truck className="w-3.5 h-3.5 text-athletic-500" />
            <span className="font-semibold text-white uppercase">Free USPS Shipping</span>
            <span className="hidden sm:inline">on every order in the USA</span>
          </p>
          <div className="flex items-center gap-4 tracking-wide">
            <span className="hidden md:flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5 text-athletic-500" /> 30-Day Returns</span>
            <a href={`mailto:${CONTACT_INFO.email}`} className="hidden lg:inline hover:text-white transition-colors">{CONTACT_INFO.email}</a>
            <a href={phoneHref} className="flex items-center gap-1.5 font-semibold text-white hover:text-athletic-500 transition-colors">
              <PhoneCall className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-white border-b-2 border-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[74px] gap-4">
            <Logo />

            {/* Desktop primary nav */}
            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${location.pathname === '/' ? 'text-athletic-600' : 'text-dark-800 hover:text-athletic-600'}`}
              >
                Home
              </Link>

              {/* Instruments dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsShopOpen(true)}
                onMouseLeave={() => setIsShopOpen(false)}
              >
                <Link
                  to="/shop"
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${location.pathname === '/shop' ? 'text-athletic-600' : 'text-dark-800 hover:text-athletic-600'}`}
                >
                  Instruments <ChevronDown className={`w-4 h-4 transition-transform ${isShopOpen ? 'rotate-180' : ''}`} />
                </Link>
                {isShopOpen && (
                  <div className="absolute left-0 top-full w-72 bg-white border-2 border-dark-900 shadow-elevated rounded-md overflow-hidden animate-fade-in">
                    <Link to="/shop" className="flex items-center justify-between px-5 py-3 bg-dark-900 text-white font-semibold uppercase tracking-wide text-sm hover:bg-dark-800">
                      Shop All <ChevronRight className="w-4 h-4 text-athletic-500" />
                    </Link>
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/shop?category=${cat.slug}`}
                        className="flex items-center justify-between px-5 py-3 text-sm text-dark-700 hover:bg-athletic-50 hover:text-athletic-700 border-t border-dark-100 transition-colors"
                      >
                        <span className="font-medium">{cat.name}</span>
                        <span className="text-xs text-dark-400">{cat.productCount}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.slice(1).map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${isActive ? 'text-athletic-600' : 'text-dark-800 hover:text-athletic-600'}`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/shop" className="hidden xl:inline-flex items-center gap-2 px-5 py-2.5 bg-athletic-500 text-white text-sm font-semibold uppercase tracking-wide rounded-md hover:bg-athletic-600 transition-colors shadow-button">
                Shop Now
              </Link>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-md flex items-center justify-center text-white bg-dark-900 hover:bg-athletic-600 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-athletic-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-md flex items-center justify-center text-dark-900 bg-dark-50 hover:bg-dark-100"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t-2 border-dark-900 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center justify-between px-4 py-3 text-base font-semibold uppercase tracking-wide text-dark-800 hover:bg-athletic-50 hover:text-athletic-600 rounded-md"
                >
                  {link.name}
                  <ChevronRight className="w-4 h-4 text-dark-300" />
                </Link>
              ))}
              <div className="pt-3 border-t border-dark-100 mt-3">
                <p className="px-4 text-xs uppercase tracking-[0.2em] text-dark-400 mb-2 flex items-center gap-2"><Music2 className="w-3.5 h-3.5 text-athletic-500" /> Instruments</p>
                <Link to="/shop" className="block px-4 py-2.5 text-sm font-semibold text-athletic-700 hover:bg-athletic-50 rounded-md">Shop All</Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-athletic-50 hover:text-athletic-600 rounded-md"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-md bg-dark-900 text-white font-semibold uppercase tracking-wide">
                <PhoneCall className="w-4 h-4 text-athletic-500" /> Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
